import React, { useState } from 'react';
import { feedbackApi } from '../services/api';
import { AnalysisResult } from '../types';
import { Modal } from './ui/modal';
import { Button } from './ui/button';

interface FeedbackButtonsProps {
  analysis: AnalysisResult;
  onFeedbackSubmitted?: (analysisId: string, agree: boolean, reasoning?: string) => void;
  /** 'pill' renders the ring-rule Agree(safe)/Disagree(ghost) feedback pills
   * used by ComparisonResultCard; 'default' keeps the plain Yes/No buttons
   * used elsewhere (e.g. AnalysisHistory). */
  variant?: 'default' | 'pill';
}

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({ analysis, onFeedbackSubmitted, variant = 'default' }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [reasoning, setReasoning] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastFeedback, setLastFeedback] = useState<{ agree: boolean } | null>(null);

  const handleFeedback = async (agree: boolean) => {
    if (agree || !agree) {
      if (!agree) {
        setShowDialog(true);
        return;
      }
      
      // Submit agreement directly
      await submitFeedback(agree, '');
    }
  };

  const submitFeedback = async (agree: boolean, reason: string) => {
    setIsSubmitting(true);
    console.log(`Submitting feedback for analysis ID: ${analysis.id}, URL: ${analysis.url}`);
    try {
      const response = await feedbackApi.submit({
        analysisId: analysis.id,
        agree,
        reasoning: reason || undefined
      });
      
      // Update local state
      analysis.feedback = {
        agree,
        reasoning: reason || undefined,
        timestamp: new Date().toISOString()
      };
      
      // Store last feedback for the success message
      setLastFeedback({ agree });
      
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted(analysis.id, agree, reason);
      }
      
      setShowDialog(false);
      setReasoning('');
      
      // Show success message
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
      
      // Log improvement message if disagreement
      if (!agree && response.message.includes('improved')) {
        console.log('✨ Prompt has been improved based on your feedback!');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogSubmit = () => {
    submitFeedback(false, reasoning);
  };

  // If feedback already exists, the submitted state replaces the Yes/No
  // buttons with the chosen badge instead of hiding the row.
  if (analysis.feedback) {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-ink">Do you agree?</span>
          <span className={`ml-auto inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${
            analysis.feedback.agree ? 'bg-safe-bg text-safe' : 'bg-risk-bg text-risk'
          }`}>
            {analysis.feedback.agree ? '✓ Agreed' : '✗ Disagreed'}
          </span>
        </div>
        {!analysis.feedback.agree && (
          <p className="text-xs text-ink-muted italic">
            ✨ This feedback was used to improve the prompt for future analysis
          </p>
        )}
      </div>
    );
  }

  return (
    <>
      {variant === 'pill' ? (
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => handleFeedback(true)}
            className="rounded-full bg-safe-bg px-3.5 py-1 text-xs font-bold text-safe hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-safe"
          >
            ✓ Agree
          </button>
          <button
            onClick={() => handleFeedback(false)}
            className="rounded-full bg-ghost-bg px-3.5 py-1 text-xs font-bold text-ink hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Disagree
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-ink">Do you agree?</span>
          <span className="ml-auto flex items-center gap-2">
            <button
              onClick={() => handleFeedback(true)}
              className="px-3 py-1 text-xs font-bold bg-safe-bg text-safe rounded-full hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-safe"
            >
              ✓ Yes
            </button>
            <button
              onClick={() => handleFeedback(false)}
              className="px-3 py-1 text-xs font-bold bg-risk-bg text-risk rounded-full hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-risk"
            >
              ✗ No
            </button>
          </span>
        </div>
      )}

      {/* Feedback Dialog — shared app-standard modal shell */}
      <Modal
        open={showDialog}
        onClose={() => !isSubmitting && setShowDialog(false)}
        title="Why do you disagree with this classification?"
      >
        <div className="mb-4">
          <p className="text-sm text-ink-muted mb-2">
            URL: <span className="font-medium">{decodeURIComponent(analysis.url)}</span>
          </p>
          <p className="text-sm text-ink-muted">
            AI Classification: <span className={`font-medium ${
              analysis.isBrandSafe ? 'text-safe' : 'text-risk'
            }`}>
              {analysis.isBrandSafe ? 'Brand Safe' : 'Not Brand Safe'}
            </span>
          </p>
        </div>

        <textarea
          value={reasoning}
          onChange={(e) => setReasoning(e.target.value)}
          placeholder="Please explain why you disagree (optional)..."
          className="w-full px-3 py-2 border border-line bg-surface text-ink rounded-md focus:ring-accent focus:border-accent"
          rows={4}
        />

        <div className="mt-4 flex justify-end space-x-3">
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => setShowDialog(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="md"
            onClick={handleDialogSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </div>

        {isSubmitting && (
          <div className="mt-4 text-center">
            <div className="inline-flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
              <p className="text-sm text-ink-muted">
                Processing feedback and improving prompt...
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Success Message */}
      {showSuccess && (
        <div className="fixed top-4 right-4 bg-safe-bg border border-safe rounded-lg p-4 z-50">
          <div className="flex items-center space-x-2">
            <div className="text-safe">✓</div>
            <p className="text-sm text-safe">
              Feedback submitted! {lastFeedback && !lastFeedback.agree && 'Prompt has been improved.'}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackButtons;